import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { LandingPageOptions } from '@app/modules/personas/interfaces/persona-landing-option.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { ToastrService } from '@app/services/toastr.service'
import { environment } from '@environments/environment'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { EventLandingService } from '../../services/event-landing.service'
import { EventService } from '@app/modules/events/services/event.service'
import { ContactCardComponent } from './contact-card/contact-card.component'
import { EventTimingsPreviewComponent } from '@app/v2/event/components/event-timings-preview/event-timings-preview.component'
import { LandingDescriptionComponent } from './landing-description/landing-description.component'
import { LandingHeaderComponent } from './landing-header/landing-header.component'
import { LandingRegistrationComponent } from './landing-registration/landing-registration.component'
import { UploadInputComponent } from './upload-input/upload-input.component'

@Component({
  selector: 'ngx-landing-previewer',
  templateUrl: './landing-previewer.component.html',
  styleUrls: ['./landing-previewer.component.scss'],
  imports: [
    CommonModule,
    LandingHeaderComponent,
    EventTimingsPreviewComponent,
    LandingDescriptionComponent,
    UploadInputComponent,
    ContactCardComponent,
    LandingRegistrationComponent
  ]
})
export class LandingPreviewerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() event: ServerRepresentedEvent
  @Input() persona: Persona
  @Output() descriptionChange = new EventEmitter<string>()
  breakpoint: string
  options: LandingPageOptions
  private _destroy$ = new Subject()
  eventId: number = this.activatedRoute.snapshot.params.event_id
  registrationLink: SafeUrl
  isLoading = true
  showOption = true
  showIfram = true
  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private landingService: EventLandingService,
    private auth: AuthService,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private eventService: EventService
  ) {}

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.persona.currentValue && changes.event.currentValue) {
      const url = `${environment.landingPageUrl}workspaces/${this.auth.currentWorkspace.slug}/events/${this.event?.slug}/forms/${this.persona?.slug}?live_update=false`
      this.registrationLink = this.sanitizer.bypassSecurityTrustResourceUrl(url)
    }
  }

  ngOnInit(): void {
    this.landingService.landingOptionChanges$.pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.showIfram = false
        setTimeout(() => {
          this.showIfram = true
        })
      }
    })
    this.landingService.landingOptions$.pipe(takeUntil(this._destroy$)).subscribe({
      next: (landingOptions: LandingPageOptions) => (this.options = landingOptions),
      error: () => this.toastr.error('Error', 'Failed to save your options updates')
    })

    this.landingService.changeBreakpoint.pipe(takeUntil(this._destroy$)).subscribe((newBreakpoint) => {
      if (newBreakpoint === undefined) {
        this.showOption = false
        setTimeout(() => {
          this.showOption = true
        }, 400)
        this.cdr.detectChanges()
      }
      if (this.breakpoint !== newBreakpoint && this.breakpoint) document.querySelector('iframe').src = document.querySelector('iframe').src
      this.breakpoint = newBreakpoint
      this.isLoading = !!newBreakpoint
      this.changeQueryParam()
    })
    this.activatedRoute.queryParams.pipe(takeUntil(this._destroy$)).subscribe((q) => {
      if (!['desktop', 'tablet', 'mobile'].includes(q.view)) return this.landingService.changeBreakpoint.next(undefined)
      this.landingService.changeBreakpoint.next(q.view)
    })
  }

  /**
   * Get registration link
   */
  registrationLinkBuilder(): SafeUrl {
    const url = `${environment.landingPageUrl}workspaces/${this.auth.currentWorkspace.slug}/events/${this.event?.slug}/forms/${this.persona?.slug}`
    return this.sanitizer.bypassSecurityTrustResourceUrl(url)
  }

  /**
   * Get bar image for each breakpoint
   */
  get breakpointBar(): string {
    return `https://library.micetribe.com/images/${this.breakpoint}-bar.svg`
  }

  /**
   * Change QueryParam of the url whenever a breakpoint is changed
   * @returns void
   */
  changeQueryParam(): void {
    this.router.navigate([], {
      queryParams: {
        view: this.breakpoint
      },
      // preserve the existing query params in the route
      queryParamsHandling: 'merge'
    })
  }

  /**
   * Disable Loading spinner when iframe is loaded
   * @returns void
   */
  disableLoading(): void {
    this.isLoading = false
  }

  /**
   * Emits the description change event
   * @param description - The new description to emit
   */
  onDescriptionChange(description: string): void {
    this.descriptionChange.emit(description)
  }

  getStaticMapUrl(): string {
    return this.eventService.getStaticMapUrl(this.event)
  }
}
